import React, { useState, useEffect } from 'react';
import { X, Minus, Plus, ShoppingBag, ArrowRight, Mail, Loader2, Tag, Shirt } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../../lib/cartContext';
import { cartApi, checkoutApi } from '../../lib/api';
import { trackBeginCheckout } from '../../lib/analytics';
import { PointsEarnLine } from '../rewards/PointsEarnLine';

const API = process.env.REACT_APP_BACKEND_URL;
const LIME = '#C7FA3E';

export const CartDrawer = () => {
  const { cart, isOpen, closeCart, loading, updateItem, removeItem } = useCart();
  const [cartEmail, setCartEmail] = useState('');
  const [emailSaved, setEmailSaved] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [teeIds, setTeeIds] = useState(null);
  const [autoApplied, setAutoApplied] = useState(false);

  useEffect(() => {
    if (!isOpen || discountCode) return;
    const saved = localStorage.getItem('ninja_triple_code');
    if (saved) {
      setDiscountCode(saved);
      setAutoApplied(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || teeIds) return;
    axios.get(`${API}/api/products?limit=100`).then(({ data }) => {
      setTeeIds(new Set((data || []).filter((p) => p.category === 'T-Shirts').map((p) => p.id)));
    }).catch(() => setTeeIds(new Set()));
  }, [isOpen, teeIds]);

  const handleCheckout = async () => {
    if (!cart?.id || checkoutLoading) return;
    setCheckoutLoading(true);
    setCheckoutError('');
    trackBeginCheckout(cart?.total || 0, (cart?.items || []).map((it) => ({
      item_id: it.product_id,
      item_name: it.name,
      price: it.price,
      quantity: it.quantity,
    })), 'cart');
    try {
      const { data } = await checkoutApi.shopifyCartUrl({
        cart_id: cart.id,
        discount_code: discountCode.trim() || null,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setCheckoutError('Could not start checkout. Please try again.');
      }
    } catch (err) {
      setCheckoutError(err?.response?.data?.detail || 'Could not start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const freeShippingThreshold = 99;
  const subtotal = cart?.total || 0;
  const remaining = Math.max(0, freeShippingThreshold - subtotal);
  const teeCount = (cart?.items || []).reduce(
    (n, it) => n + (teeIds?.has(it.product_id) ? it.quantity : 0),
    0
  );

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in"
        onClick={closeCart}
        data-testid="cart-backdrop"
      />

      <div
        className="fixed top-0 right-0 h-full w-full max-w-md bg-[#0A0A0A] border-l border-zinc-800 z-50 animate-slide-in-right flex flex-col"
        data-testid="cart-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <h2
            className="text-xl uppercase tracking-wider text-white"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Your Cart
          </h2>
          <button
            onClick={closeCart}
            className="p-2 text-zinc-400 hover:text-white transition-colors duration-300"
            data-testid="close-cart-btn"
            aria-label="Close cart"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Free Shipping Progress */}
        {cart && cart.items.length > 0 && (
          <div className="px-6 py-3 border-b border-zinc-800/50">
            {remaining > 0 ? (
              <>
                <p className="text-xs text-zinc-400 mb-2">
                  Add <span className="text-[#E63946] font-semibold">${remaining.toFixed(2)}</span> more for free shipping
                </p>
                <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#E63946] transition-all duration-500"
                    style={{ width: `${Math.min(100, (subtotal / freeShippingThreshold) * 100)}%` }}
                  />
                </div>
              </>
            ) : (
              <p className="text-xs text-green-400 font-medium">You qualify for free shipping!</p>
            )}
          </div>
        )}

        {/* 3-for-$75 tee bundle nudge */}
        {cart && cart.items.length > 0 && teeIds && teeCount > 0 && (
          <div className="px-6 py-3 border-b border-zinc-800/50" data-testid="cart-bundle-nudge">
            {teeCount >= 3 ? (
              <p className="text-xs font-medium flex items-center gap-1.5" style={{ color: LIME }} data-testid="cart-bundle-unlocked">
                <Shirt className="w-3.5 h-3.5" /> 3-tee bundle unlocked — enter your TRIPLE code below for ~$75
              </p>
            ) : (
              <>
                <p className="text-xs text-zinc-400 mb-2 flex items-center gap-1.5" data-testid="cart-bundle-progress-text">
                  <Shirt className="w-3.5 h-3.5" style={{ color: LIME }} />
                  Add <span className="font-semibold" style={{ color: LIME }}>{3 - teeCount} more tee{3 - teeCount > 1 ? 's' : ''}</span>
                  &nbsp;to unlock your <span className="font-semibold" style={{ color: LIME }}>$75 bundle</span>
                </p>
                <div className="flex gap-1 mb-1.5">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="h-1 flex-1 rounded-full transition-colors duration-500"
                      style={{ backgroundColor: i < teeCount ? LIME : '#27272a' }}
                    />
                  ))}
                </div>
                <p className="text-[10px] text-zinc-600">
                  Any 3 tees ≈ $75 with a TRIPLE code —{' '}
                  <Link to="/shop" onClick={closeCart} className="underline hover:text-zinc-400" data-testid="cart-bundle-shop-link">add tees</Link>
                  {' · '}
                  <Link to="/#three-for-75" onClick={closeCart} className="underline hover:text-zinc-400" data-testid="cart-bundle-code-link">get a code</Link>
                </p>
              </>
            )}
          </div>
        )}

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {!cart || cart.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag className="w-16 h-16 text-zinc-700 mb-4" />
              <p className="text-zinc-500 mb-2">Your cart is empty</p>
              <p className="text-zinc-600 text-sm">Add some gear to get started</p>
            </div>
          ) : (
            <div className="space-y-6">
              {cart.items.map((item) => (
                <div
                  key={`${item.product_id}-${item.variant_id || 'default'}`}
                  className="flex gap-4 pb-6 border-b border-zinc-800"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <div className="w-20 h-20 bg-zinc-900 flex-shrink-0 overflow-hidden">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-700">
                        <ShoppingBag className="w-8 h-8" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-white text-sm font-medium truncate mb-0.5">{item.name}</h3>
                    {item.variant && (
                      <p className="text-zinc-500 text-xs mb-1" data-testid={`cart-item-variant-${item.product_id}`}>{item.variant}</p>
                    )}
                    <p className="text-[#E63946] text-sm font-semibold mb-3">${item.price.toFixed(2)}</p>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateItem(item.product_id, item.quantity - 1, item.variant_id)}
                        disabled={loading || item.quantity <= 1}
                        className="w-8 h-8 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors duration-300 disabled:opacity-50"
                        data-testid={`decrease-qty-${item.product_id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center text-white text-sm">{item.quantity}</span>
                      <button
                        onClick={() => updateItem(item.product_id, item.quantity + 1, item.variant_id)}
                        disabled={loading}
                        className="w-8 h-8 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors duration-300 disabled:opacity-50"
                        data-testid={`increase-qty-${item.product_id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeItem(item.product_id, item.variant_id)}
                        disabled={loading}
                        className="ml-auto text-zinc-500 hover:text-[#E63946] text-sm transition-colors duration-300"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart && cart.items.length > 0 && (
          <div className="p-6 border-t border-zinc-800 bg-black">
            {/* Email capture for cart recovery */}
            {!emailSaved && (
              <div className="mb-4" data-testid="cart-email-capture">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                    <input
                      type="email"
                      value={cartEmail}
                      onChange={e => setCartEmail(e.target.value)}
                      placeholder="Email for order updates"
                      className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-700 text-white text-xs placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors"
                      data-testid="cart-email-input"
                    />
                  </div>
                  <button
                    onClick={async () => {
                      if (cartEmail && cart?.id) {
                        try {
                          await cartApi.associateEmail(cart.id, cartEmail);
                          setEmailSaved(true);
                        } catch (e) { /* silent */ }
                      }
                    }}
                    disabled={!cartEmail}
                    className="px-3 py-2 bg-zinc-800 text-zinc-300 text-xs hover:bg-zinc-700 transition-colors disabled:opacity-40"
                    data-testid="cart-email-save"
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
            {emailSaved && (
              <p className="text-green-500 text-xs mb-3 flex items-center gap-1" data-testid="cart-email-saved">
                <Mail className="w-3 h-3" /> We'll email you order updates
              </p>
            )}

            <div className="flex justify-between items-center mb-4">
              <span className="text-zinc-400 uppercase tracking-wider text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Subtotal
              </span>
              <span className="text-white text-xl font-semibold" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>

            {/* Points earn incentive */}
            <PointsEarnLine amount={subtotal} className="mb-4" testId="cart-points-earn" />

            {/* Discount / rewards code — applied on Shopify's secure checkout */}
            <div className="mb-4" data-testid="cart-discount">
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                <input
                  type="text"
                  value={discountCode}
                  onChange={e => { setDiscountCode(e.target.value.toUpperCase()); setAutoApplied(false); }}
                  placeholder="Discount or rewards code (optional)"
                  className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-700 text-white text-xs placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors uppercase tracking-wide"
                  data-testid="cart-discount-input"
                />
              </div>
              {autoApplied && discountCode ? (
                <p className="text-[10px] mt-1.5" style={{ color: '#C7FA3E' }} data-testid="cart-discount-autofilled">
                  Your 3-for-$75 code was auto-filled — one-time use, applied on the secure checkout page.
                </p>
              ) : (
                <p className="text-zinc-600 text-[10px] mt-1.5">Code is applied on the secure Shopify checkout page.</p>
              )}
            </div>

            <p className="text-zinc-600 text-xs mb-3">
              Shipping &amp; returns coverage calculated at checkout
            </p>

            {checkoutError && (
              <p className="text-[#E63946] text-xs mb-3" data-testid="cart-checkout-error">{checkoutError}</p>
            )}

            <button
              onClick={handleCheckout}
              disabled={loading || checkoutLoading}
              className="btn-ninja w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="checkout-btn"
            >
              {checkoutLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
              ) : (
                <>Checkout <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
